import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Plus, Video, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { adminUploadImage } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const inputCls =
  "mt-1.5 w-full rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition";

const rowCls =
  "rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition";

const UploadBtn = ({ onUploaded, testid, accept = "image/*", label = "Upload", icon: Icon = ImagePlus }) => {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await adminUploadImage(localStorage.getItem("classina_admin"), file);
      onUploaded(url);
      toast.success(accept.startsWith("video") ? "Video uploaded" : "Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — please try again");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={onFile} data-testid={`${testid}-input`} />
      <button
        type="button"
        data-testid={testid}
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="shrink-0 rounded-md border border-border bg-secondary/60 px-3 py-2.5 text-xs font-bold flex items-center gap-1.5 hover:bg-secondary transition disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />} {label}
      </button>
    </>
  );
};

const emptyForm = {
  name: "",
  slug: "",
  category: "",
  price: "",
  mrp: "",
  tagline: "",
  description: "",
  specs: "",
  badge: "",
  image: "",
  images: [],
  video: "",
  min_qty: "1",
  stock: "100",
  sku: "",
  attributes: "",
  variants: [],
  packs: [],
};

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/[\s_]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");

export const ProductFormDialog = ({ open, onOpenChange, product, categories, onSave, saving }) => {
  const [form, setForm] = useState(emptyForm);
  const editing = !!product;

  useEffect(() => {
    if (!open) return;
    if (product) {
      setForm({
        name: product.name || "",
        slug: product.slug || "",
        category: product.category || "",
        price: product.price ?? "",
        mrp: product.mrp ?? "",
        tagline: product.tagline || "",
        description: product.description || "",
        specs: (product.specs || []).join("\n"),
        badge: product.badge || "",
        image: product.image || "",
        images: [...(product.images || [])],
        video: product.video || "",
        min_qty: String(product.min_qty ?? 1),
        stock: String(product.stock ?? 100),
        sku: product.sku || "",
        attributes: Object.entries(product.attributes || {}).map(([k, v]) => `${k}: ${v}`).join("\n"),
        variants: (product.variants || []).map((v) => ({ name: v.name, image: v.image || "" })),
        packs: (product.packs || []).map((p) => ({ pieces: String(p.pieces), price: String(p.price) })),
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, product]);

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v, ...(k === "name" && !editing ? { slug: slugify(v) } : {}) }));
  };

  const setVariant = (i, k) => (e) =>
    setForm((f) => ({ ...f, variants: f.variants.map((v, idx) => (idx === i ? { ...v, [k]: e.target.value } : v)) }));

  const setImage = (i) => (e) =>
    setForm((f) => ({ ...f, images: f.images.map((img, idx) => (idx === i ? e.target.value : img)) }));

  const setPack = (i, k) => (e) =>
    setForm((f) => ({ ...f, packs: f.packs.map((p, idx) => (idx === i ? { ...p, [k]: e.target.value } : p)) }));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...(editing ? {} : { slug: form.slug }),
      name: form.name.trim(),
      category: form.category.trim(),
      price: Number(form.price),
      mrp: form.mrp ? Number(form.mrp) : null,
      tagline: form.tagline.trim(),
      description: form.description.trim(),
      specs: form.specs.split("\n").map((s) => s.trim()).filter(Boolean),
      badge: form.badge.trim() || null,
      image: form.image.trim(),
      images: form.images.map((i) => i.trim()).filter(Boolean),
      video: form.video.trim() || null,
      min_qty: Math.max(1, Number(form.min_qty) || 1),
      stock: Math.max(0, Number(form.stock) || 0),
      sku: form.sku.trim() || null,
      attributes: Object.fromEntries(
        form.attributes
          .split("\n")
          .map((l) => l.split(":"))
          .filter(([k, ...rest]) => k.trim() && rest.join(":").trim())
          .map(([k, ...rest]) => [k.trim(), rest.join(":").trim()]),
      ),
      variants: form.variants.filter((v) => v.name.trim()),
      packs: form.packs
        .filter((p) => Number(p.pieces) > 0 && Number(p.price) > 0)
        .map((p) => ({ label: `${Number(p.pieces)} pc pack`, pieces: Number(p.pieces), price: Number(p.price) })),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-form-dialog" className="max-w-2xl bg-background max-h-[90vh] overflow-y-auto">
        <DialogTitle className="font-serif text-2xl md:text-3xl font-semibold tracking-tight">
          {editing ? `Edit — ${product.name}` : "Add a new product"}
        </DialogTitle>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Product name</span>
              <input data-testid="pf-name" required value={form.name} onChange={set("name")} placeholder="Classina A3 Sketch Pad" className={inputCls} />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Slug (URL id)</span>
              <input
                data-testid="pf-slug"
                required
                value={form.slug}
                onChange={set("slug")}
                disabled={editing}
                placeholder="a3-sketch-pad"
                className={`${inputCls} disabled:opacity-50 font-mono text-xs`}
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Category</span>
              <input data-testid="pf-category" required list="pf-categories" value={form.category} onChange={set("category")} placeholder="Creative" className={inputCls} />
              <datalist id="pf-categories">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Badge (optional)</span>
              <input data-testid="pf-badge" value={form.badge} onChange={set("badge")} placeholder="New / Bestseller / Popular" className={inputCls} />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Min. set size (pcs)</span>
              <input data-testid="pf-min-qty" type="number" min="1" value={form.min_qty} onChange={set("min_qty")} placeholder="1" className={inputCls} />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Stock (pcs)</span>
              <input data-testid="pf-stock" type="number" min="0" value={form.stock} onChange={set("stock")} placeholder="100" className={inputCls} />
              <span className="text-[10px] text-muted-foreground mt-1 block">Set to 0 to show "Stock coming soon" on the store. Orders reduce this automatically.</span>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">SKU / Product code (optional)</span>
              <input data-testid="pf-sku" value={form.sku} onChange={set("sku")} placeholder="e.g. CLS-A5-172" className={`${inputCls} font-mono`} />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Selling price (₹)</span>
              <input data-testid="pf-price" required type="number" min="1" value={form.price} onChange={set("price")} placeholder="149" className={inputCls} />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">MRP (₹, optional)</span>
              <input data-testid="pf-mrp" type="number" min="1" value={form.mrp} onChange={set("mrp")} placeholder="180" className={inputCls} />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Tagline</span>
            <input data-testid="pf-tagline" value={form.tagline} onChange={set("tagline")} placeholder="Write Green, Live Clean" className={inputCls} />
          </label>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Description</span>
            <textarea data-testid="pf-description" rows={3} value={form.description} onChange={set("description")} placeholder="What makes this product special…" className={`${inputCls} resize-none`} />
          </label>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Specs (one per line)</span>
            <textarea data-testid="pf-specs" rows={3} value={form.specs} onChange={set("specs")} placeholder={"A4 size · 160 pages\n140 GSM paper"} className={`${inputCls} resize-none font-mono text-xs`} />
          </label>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Specification table (Key: value per line)</span>
            <textarea
              data-testid="pf-attributes"
              rows={4}
              value={form.attributes}
              onChange={set("attributes")}
              placeholder={"Size: A5\nPages: 172\nPaper GSM: 58\nRuling: Single Line\nBinding: Centre-stitched\nCover Material: Kraft board"}
              className={`${inputCls} resize-none font-mono text-xs`}
            />
          </label>

          <div>
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Main image</span>
            <div className="flex gap-2 items-center mt-1.5">
              {form.image && <img src={form.image} alt="preview" className="w-11 h-11 rounded-lg object-cover border border-border shrink-0" />}
              <input data-testid="pf-image" required value={form.image} onChange={set("image")} placeholder="https://… or upload →" className={`${inputCls} mt-0 flex-1`} />
              <UploadBtn testid="pf-upload-main" onUploaded={(url) => setForm((f) => ({ ...f, image: url }))} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">More photos (optional)</span>
              <button
                type="button"
                data-testid="pf-add-image"
                onClick={() => setForm((f) => ({ ...f, images: [...f.images, ""] }))}
                className="text-xs font-bold text-primary flex items-center gap-1 hover:underline"
              >
                <Plus className="w-3.5 h-3.5" /> Add photo
              </button>
            </div>
            {form.images.map((img, i) => (
              <div key={i} className="flex gap-2 mt-2 items-center">
                {img && <img src={img} alt="" className="w-9 h-9 rounded-md object-cover border border-border shrink-0" />}
                <input data-testid={`pf-image-${i}`} value={img} onChange={setImage(i)} placeholder={`Photo ${i + 2} URL`} className={`${rowCls} flex-1`} />
                <UploadBtn
                  testid={`pf-upload-photo-${i}`}
                  onUploaded={(url) => setForm((f) => ({ ...f, images: f.images.map((x, idx) => (idx === i ? url : x)) }))}
                />
                <button
                  type="button"
                  data-testid={`pf-remove-image-${i}`}
                  onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))}
                  className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                  aria-label="Remove photo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div>
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Video (optional — upload mp4 or paste a YouTube link)</span>
            <div className="flex gap-2 mt-1.5 items-center">
              <input data-testid="pf-video" value={form.video} onChange={set("video")} placeholder="https://… or upload your video file →" className={`${inputCls} mt-0 flex-1`} />
              <UploadBtn testid="pf-upload-video" accept="video/*" label="Upload video" icon={Video} onUploaded={(url) => setForm((f) => ({ ...f, video: url }))} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">Upload the video file straight from your computer (mp4, up to 100 MB) — no YouTube needed. Buyers will see a VIDEO button on the product page.</p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Multi-packs (optional — e.g. 24 pc pack)</span>
              <button
                type="button"
                data-testid="pf-add-pack"
                onClick={() => setForm((f) => ({ ...f, packs: [...f.packs, { pieces: "", price: "" }] }))}
                className="text-xs font-bold text-primary flex items-center gap-1 hover:underline"
              >
                <Plus className="w-3.5 h-3.5" /> Add pack
              </button>
            </div>
            {form.packs.map((p, i) => (
              <div key={i} className="grid grid-cols-[6rem_1fr_auto_auto] gap-2 mt-2 items-center">
                <input data-testid={`pf-pack-pieces-${i}`} type="number" min="2" value={p.pieces} onChange={setPack(i, "pieces")} placeholder="Pcs" className={rowCls} />
                <input data-testid={`pf-pack-price-${i}`} type="number" min="1" value={p.price} onChange={setPack(i, "price")} placeholder="Pack price ₹" className={rowCls} />
                <span className="text-xs font-bold text-muted-foreground whitespace-nowrap">{p.pieces ? `${p.pieces} pc pack` : ""}</span>
                <button
                  type="button"
                  data-testid={`pf-remove-pack-${i}`}
                  onClick={() => setForm((f) => ({ ...f, packs: f.packs.filter((_, idx) => idx !== i) }))}
                  className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                  aria-label="Remove pack"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {form.packs.length > 0 && (
              <p className="text-[11px] text-muted-foreground mt-2">Buyers see the pack price with automatic savings vs buying singles.</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Variants — colours / rulings (optional)</span>
              <button
                type="button"
                data-testid="pf-add-variant"
                onClick={() => setForm((f) => ({ ...f, variants: [...f.variants, { name: "", image: "" }] }))}
                className="text-xs font-bold text-primary flex items-center gap-1 hover:underline"
              >
                <Plus className="w-3.5 h-3.5" /> Add variant
              </button>
            </div>
            {form.variants.map((v, i) => (
              <div key={i} className="grid grid-cols-[10rem_1fr_auto] gap-2 mt-2 items-center">
                <input data-testid={`pf-variant-name-${i}`} value={v.name} onChange={setVariant(i, "name")} placeholder="Colour / ruling name" className={rowCls} />
                <input data-testid={`pf-variant-image-${i}`} value={v.image} onChange={setVariant(i, "image")} placeholder="Image URL (optional)" className={rowCls} />
                <UploadBtn
                  testid={`pf-upload-variant-${i}`}
                  onUploaded={(url) =>
                    setForm((f) => ({ ...f, variants: f.variants.map((x, idx) => (idx === i ? { ...x, image: url } : x)) }))
                  }
                />
                <button
                  type="button"
                  data-testid={`pf-remove-variant-${i}`}
                  onClick={() => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }))}
                  className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                  aria-label="Remove variant"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <button
            data-testid="pf-submit"
            type="submit"
            disabled={saving}
            className="w-full rounded-full bg-primary text-primary-foreground py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? "Saving…" : editing ? "Save changes" : "Add product"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
